import type { HostedEvent } from "@hobbyshopify/backend-client";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import {
  cancelHostedEvent,
  loadHostedEvent,
  publishHostedEvent,
  retryHostedEventRefunds,
  unpublishHostedEvent,
  updateHostedEvent,
  uploadHostedEventFlyer,
} from "../../core/ops/hosted-event-ops";
import {
  FLYER_REMOVE_FAILED_ON_THE_WAY,
  FLYER_UNREADABLE,
  FLYER_UPLOAD_FAILED_ON_THE_WAY,
  flyerDataUrl,
} from "../../core/primitives/flyer-image";
import {
  CANCEL_FAILED_ON_THE_WAY,
  cancelledText,
  PUBLISH_FAILED_ON_THE_WAY,
  REFUNDS_FAILED_ON_THE_WAY,
  retriedText,
  SHOW_FAILED_TO_LOAD,
  SHOW_MAY_NOTHING,
  showDates,
  showMay,
} from "../../core/primitives/hosted-event";
import {
  busyForm,
  failedForm,
  failedLoad,
  idleForm,
  loaded,
  loading,
  savedForm,
} from "../../core/primitives/load-status";
import { paidTables } from "../../core/primitives/table-kind";
import { TableKindsSectionController } from "./sections/table-kinds/table-kinds-section-controller";
import { TicketKindsSectionController } from "./sections/ticket-kinds/ticket-kinds-section-controller";
import { ShowActions, ShowState } from "./show-model";

export type ShowDeps = Pick<AppDeps, "client" | "authHandler" | "storeRepository" | "shareLinks">;

export interface ShowParams {
  eventId: string;
}

export interface ShowRouting {
  toList(): void;
  toEdit(eventId: string): void;
}

/**
 * A show's own page, where its host lands after creating it: the tables it
 * sells and who took them, publishing, and calling the show off, which refunds
 * its tables. The show's details are changed on the form behind "Edit".
 */
export class ShowController extends SubtreeController implements ShowActions {
  readonly state = new ShowState();
  readonly tables: TableKindsSectionController;
  readonly tickets: TicketKindsSectionController;
  readonly now: () => Date;
  readonly prepareFlyer: (file: File) => Promise<string>;
  #storeId: string | null = null;
  /**
   * A reading asked for after a call failed is numbered when it is asked for,
   * an answer when it lands. A lower number never lands over a higher one.
   */
  #asked = 0;
  #landed = 0;

  constructor(
    readonly deps: ShowDeps,
    readonly params: ShowParams,
    readonly routing: ShowRouting,
    {
      now = () => new Date(),
      prepareFlyer = flyerDataUrl,
    }: { now?: () => Date; prepareFlyer?: (file: File) => Promise<string> } = {},
  ) {
    super();
    this.subtree.put(ShowState, this.state);
    this.subtree.put(ShowActions, this);
    this.sync(() => {
      const event = this.state.event.value;
      const listed = event?.isPublished && !event.isBlocked && !event.cancelledAt;
      this.state.share.value = event && listed ? deps.shareLinks.show(event, showDates(event)) : null;
    }, [this.state.event]);
    this.now = now;
    this.prepareFlyer = prepareFlyer;
    const tables = this.own(
      new TableKindsSectionController(this.subtree, deps, {
        eventId: params.eventId,
        cancelledAt: this.state.cancelledAt,
      }),
    );
    this.tables = tables;
    this.tickets = this.own(
      new TicketKindsSectionController(this.subtree, deps, {
        eventId: params.eventId,
        cancelledAt: this.state.cancelledAt,
      }),
    );
    this.sync(() => {
      const read = tables.state.status.value.kind === "loaded";
      this.state.paidTables.value = read ? paidTables(tables.state.kinds.value) : null;
      this.#offer();
    }, [tables.state.kinds, tables.state.status]);
    this.sync(() => {
      const form = this.state.cancelForm.value;
      this.state.cancelOffered.value =
        this.state.may.value.cancel || this.state.confirmCancel.value || form.error !== null;
    }, [this.state.may, this.state.confirmCancel, this.state.cancelForm]);
    this.sync(() => {
      this.state.acting.value = this.#acting();
    }, [this.state.publishForm, this.state.cancelForm, this.state.retryForm, this.state.flyerForm]);
    this.sync(() => {
      const storeId = deps.storeRepository.shopId;
      if (storeId === this.#storeId) return;
      this.#storeId = storeId;
      this.#forget();
      if (storeId) void this.#load(storeId);
    }, [deps.storeRepository]);
  }

  back = () => {
    this.routing.toList();
  };

  edit = () => {
    this.routing.toEdit(this.params.eventId);
  };

  publish = () => this.#setPublished(true);

  unpublish = () => this.#setPublished(false);

  cancelShow = async () => {
    const storeId = this.#storeId;
    const eventId = this.params.eventId;
    if (!storeId || this.#acting()) return;
    if (!this.state.cancelledAt.value && !this.state.confirmCancel.value) {
      this.state.confirmCancel.value = true;
      return;
    }
    this.state.cancelForm.value = busyForm;
    let r: Awaited<ReturnType<typeof cancelHostedEvent>>;
    try {
      r = await cancelHostedEvent({ ...this.deps, storeId, eventId });
    } catch (err) {
      // Part of it may have happened: read the show and its tables again, and let the error reach Sentry.
      if (!this.isDisposed && storeId === this.#storeId) {
        this.state.confirmCancel.value = false;
        this.state.cancelForm.value = failedForm(CANCEL_FAILED_ON_THE_WAY);
        void this.#refresh(storeId);
      }
      throw err;
    }
    if (this.isDisposed || storeId !== this.#storeId) return;
    this.state.confirmCancel.value = false;
    if (!r.ok) {
      this.state.cancelForm.value = failedForm(r.errors);
      return;
    }
    this.#set(r.value.event);
    const outcome = cancelledText(r.value);
    this.state.cancelForm.value = r.value.refundsFailed > 0 ? failedForm(outcome) : savedForm(outcome);
    void this.tables.reload();
  };

  keepShow = () => {
    if (this.state.cancelForm.value.busy) return;
    this.state.confirmCancel.value = false;
    this.state.cancelForm.value = idleForm;
  };

  retryRefunds = async () => {
    const storeId = this.#storeId;
    const eventId = this.params.eventId;
    if (!storeId || this.#acting()) return;
    this.state.retryForm.value = busyForm;
    let r: Awaited<ReturnType<typeof retryHostedEventRefunds>>;
    try {
      r = await retryHostedEventRefunds({ ...this.deps, storeId, eventId });
    } catch (err) {
      if (!this.isDisposed && storeId === this.#storeId) {
        this.state.retryForm.value = failedForm(REFUNDS_FAILED_ON_THE_WAY);
        void this.#refresh(storeId);
      }
      throw err;
    }
    if (this.isDisposed || storeId !== this.#storeId) return;
    if (!r.ok) {
      this.state.retryForm.value = failedForm(r.errors);
      return;
    }
    this.#set(r.value.event);
    const outcome = retriedText(r.value);
    this.state.retryForm.value = r.value.refundsFailed > 0 ? failedForm(outcome) : savedForm(outcome);
    void this.tables.reload();
  };

  pickFlyer = async (file: File) => {
    const storeId = this.#storeId;
    if (!storeId || this.#acting() || !this.state.may.value.edit) return;
    this.state.flyerForm.value = busyForm;
    let image: string;
    try {
      image = await this.prepareFlyer(file);
    } catch (err) {
      if (!this.isDisposed)
        this.state.flyerForm.value = failedForm(err instanceof Error ? err.message : FLYER_UNREADABLE);
      return;
    }
    if (this.isDisposed || storeId !== this.#storeId) return;
    await this.#changeFlyer(
      storeId,
      () => uploadHostedEventFlyer({ ...this.deps, storeId, eventId: this.params.eventId, image }),
      FLYER_UPLOAD_FAILED_ON_THE_WAY,
    );
  };

  removeFlyer = async () => {
    const storeId = this.#storeId;
    if (!storeId || this.#acting() || !this.state.may.value.edit || !this.state.event.value?.flyerUrl) return;
    this.state.flyerForm.value = busyForm;
    await this.#changeFlyer(
      storeId,
      () => updateHostedEvent({ ...this.deps, storeId, eventId: this.params.eventId, body: { flyerUrl: null } }),
      FLYER_REMOVE_FAILED_ON_THE_WAY,
    );
  };

  async #changeFlyer(
    storeId: string,
    call: () => ReturnType<typeof uploadHostedEventFlyer>,
    onTheWay: string,
  ): Promise<void> {
    let r: Awaited<ReturnType<typeof call>>;
    try {
      r = await call();
    } catch (err) {
      if (!this.isDisposed && storeId === this.#storeId) {
        this.state.flyerForm.value = failedForm(onTheWay);
        void this.#refresh(storeId, { tables: false });
      }
      throw err;
    }
    if (this.isDisposed || storeId !== this.#storeId) return;
    if (!r.ok) {
      this.state.flyerForm.value = failedForm(r.errors);
      return;
    }
    this.#set(r.value);
    this.state.flyerForm.value = idleForm;
  }

  async #setPublished(publish: boolean): Promise<void> {
    const storeId = this.#storeId;
    if (!storeId || this.#acting()) return;
    this.state.publishForm.value = busyForm;
    const call = publish ? publishHostedEvent : unpublishHostedEvent;
    let r: Awaited<ReturnType<typeof call>>;
    try {
      r = await call({ ...this.deps, storeId, eventId: this.params.eventId });
    } catch (err) {
      // It may have happened: read the show again, and let the error reach Sentry.
      if (!this.isDisposed && storeId === this.#storeId) {
        this.state.publishForm.value = failedForm(PUBLISH_FAILED_ON_THE_WAY);
        void this.#refresh(storeId, { tables: false });
      }
      throw err;
    }
    if (this.isDisposed || storeId !== this.#storeId) return;
    if (!r.ok) {
      this.state.publishForm.value = failedForm(r.errors);
      return;
    }
    this.#set(r.value);
    // What a show taken down is offered hangs on the tables paid for: count them as they are now.
    if (!publish) void this.tables.reload();
    const said = publish ? "Published." : "Back to a draft.";
    this.state.publishForm.value = r.value.cancelledAt ? idleForm : savedForm(said);
  }

  /** One call at a time, so the answer to one is never overtaken by another's. */
  #acting(): boolean {
    const { publishForm, cancelForm, retryForm, flyerForm } = this.state;
    return publishForm.value.busy || cancelForm.value.busy || retryForm.value.busy || flyerForm.value.busy;
  }

  /**
   * Puts a new reading of the show on the page, and drops the sentences it
   * has made untrue: a cancel's "try again" once the show is cancelled, any
   * complaint about refunds once it owes none, and what publishing said about
   * a show since cancelled.
   */
  #set(event: HostedEvent, reading = ++this.#asked): void {
    if (reading < this.#landed) return;
    this.#landed = reading;
    this.state.event.value = event;
    this.#offer();
    this.state.cancelledAt.value = event.cancelledAt;
    const paidUp = event.refundsOwed.length === 0;
    const cancelError = this.state.cancelForm.value.error;
    if (cancelError && event.cancelledAt && (cancelError === CANCEL_FAILED_ON_THE_WAY || paidUp)) {
      this.state.cancelForm.value = idleForm;
    }
    if (this.state.retryForm.value.error && paidUp) this.state.retryForm.value = idleForm;
    if (event.cancelledAt && !this.state.publishForm.value.busy) this.state.publishForm.value = idleForm;
  }

  /** What the page offers follows the show as last read and the paid tables as last counted. */
  #offer(): void {
    const event = this.state.event.value;
    if (event) this.state.may.value = showMay(event, this.now(), this.state.paidTables.value);
  }

  /** Another shop's show is another page: nothing of the last one stays. */
  #forget(): void {
    this.state.event.value = null;
    this.state.may.value = SHOW_MAY_NOTHING;
    this.state.cancelledAt.value = null;
    this.state.confirmCancel.value = false;
    this.state.publishForm.value = idleForm;
    this.state.cancelForm.value = idleForm;
    this.state.retryForm.value = idleForm;
    this.state.flyerForm.value = idleForm;
  }

  /**
   * What may have changed about the show without the page knowing, after a
   * call that failed on its way. An answer or a reading asked for after
   * this one is newer, and stays.
   */
  async #refresh(storeId: string, { tables = true }: { tables?: boolean } = {}): Promise<void> {
    if (tables) void this.tables.reload();
    const reading = ++this.#asked;
    const r = await loadHostedEvent({ ...this.deps, storeId, eventId: this.params.eventId });
    if (this.isDisposed || storeId !== this.#storeId || !r.ok) return;
    this.#set(r.value, reading);
  }

  async #load(storeId: string): Promise<void> {
    this.state.status.value = loading;
    let r: Awaited<ReturnType<typeof loadHostedEvent>>;
    try {
      r = await loadHostedEvent({ ...this.deps, storeId, eventId: this.params.eventId });
    } catch (err) {
      if (!this.isDisposed && storeId === this.#storeId) {
        this.state.status.value = { kind: "error", message: SHOW_FAILED_TO_LOAD };
      }
      throw err;
    }
    if (this.isDisposed || storeId !== this.#storeId) return;
    if (!r.ok) {
      this.state.status.value = failedLoad(r.errors);
      return;
    }
    this.#set(r.value);
    this.state.status.value = loaded;
  }
}
